import { strIsNullOrEmpty, strReplaceAll } from '@fox/shared';

export const VOWELS = ['a', 'e', 'i', 'o', 'u'];

export function toTitleCase(str: string) {
	return str.replace(/\w\S*/g, function (txt) {
		return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
	});
}

export function strTrim(str: string) {
	if (strIsNullOrEmpty(str)) {
		return str;
	}
	if (typeof String.prototype.trim !== 'function') {
		return str.replace(/^\s+|\s+$/g, '');
	}
	return str.trim();
}

/**
 * Truncates the given string to the specified maximum length.
 *
 * @param str The string to truncate.
 * @param maxLength The maximum length to truncate the string to.
 * @returns If the string exceeds the maximum length, it is truncated to that length.
 * If the string is shorter than or equal to the maximum length, it is returned unchanged.
 */
export function strTruncate(str: string, maxLength: number) {
	if (str.length > maxLength) {
		return str.substring(0, maxLength);
	}
	return str;
}

export const capitalize = (s: string) => {
	if (typeof s !== 'string') return '';
	return s.charAt(0).toUpperCase() + s.slice(1);
};

/**
 * Removes all spaces from a string including non breaking spaces
 * @param input
 * @returns
 */
export function strRemoveAllSpaces(input: string) {
	if (typeof input !== 'string') return '';
	return input.replace(new RegExp(`[\\uFEFF${NBSP}\\s]`, 'g'), '');
}

export const NBSP = String.fromCharCode(160);

//the tryparse functions also live in foxd, but it will be a bit of a pain to change every single import, will do later at some point

/**
 * Parse an object from a json string, noticed we often do this and have to check whether or not a string is actually json.
 * @param {string} jsonString - The string that is supposedly stringified json.
 * @param {TDefault} defaultValue - The default value that should be returned if the object is not an valid JSON string containing an object
 *
 * If parsing fails or we error in any way, defaultValue is returned, e.g. you pass in an array.
 * @defaultValue undefined
 */
export function tryParseJSONObject<T extends object>(jsonString: string, defaultValue: T): T;
export function tryParseJSONObject<T extends object, TDefault extends Partial<T> = Partial<T>>(
	jsonString: string | undefined | null,
	defaultValue: TDefault
): TDefault;
// todo change this to any and update usages to specify types
export function tryParseJSONObject<T = any>(jsonString: string | undefined | null): T | undefined;
// implementation (signature not exposed)
export function tryParseJSONObject<T>(jsonString: string | undefined | null, defaultValue?: T) {
	if (!jsonString) return defaultValue;

	if (typeof jsonString === 'string') {
		try {
			var o = JSON.parse(jsonString);

			if (o && typeof o === 'object') {
				return o;
			}
		} catch (e) {}
	}

	return defaultValue;
}

/**
 * Parse an object from a json string, noticed we often do this and have to check whether or not a string is actually json.
 * @param {string} jsonString - The string that is supposedly stringified json.
 * @param {TDefault} defaultValue - The default value that should be returned if the object is not an valid JSON string containing an object
 *
 * If parsing fails or we error in any way, defaultValue is returned, e.g. you pass in an array.
 * @defaultValue undefined
 */
export function tryParseJSONArray<TItem>(jsonString: string, defaultValue: TItem[]): TItem[];
export function tryParseJSONArray<TItem>(jsonString: string | undefined | null, defaultValue: TItem[]): TItem[];
export function tryParseJSONArray<TItem>(jsonString: string | undefined | null, defaultValue?: TItem[]): TItem[] | undefined;
export function tryParseJSONArray<TItem>(jsonString: string | undefined | null, defaultValue?: TItem[]) {
	if (typeof jsonString === 'string') {
		try {
			var o = JSON.parse(jsonString);

			if (Array.isArray(o)) {
				return o;
			}
		} catch (e) {}
	}

	return defaultValue;
}

export function nodeIsNullOrEmpty(node: React.ReactNode): node is null | undefined | '' {
	if (typeof node === 'string') {
		return strIsNullOrEmpty(node);
	}
	return node === null || node === undefined;
}

/**
 * Append a plural or singular phrase to a value.
 * @param {number} value - The value for the string to be appended to.
 * @param options - Configuration for pluralization
 */
export function pluralize(
	value: number,
	options: { single: string; plural: string; hideValue?: boolean } | { single: (value: number) => string; plural: (value: number) => string }
) {
	if (value === 1) {
		if (typeof options.single === 'function') {
			return options.single(value);
		} else if (options.hasOwnProperty('hideValue')) {
			return options.single;
		} else {
			return `${value} ${options.single}`;
		}
	} else if (value > 1) {
		if (typeof options.plural === 'function') {
			return options.plural(value);
		} else if (options.hasOwnProperty('hideValue')) {
			return options.plural;
		} else {
			return `${value} ${options.plural}`;
		}
	} else {
		if (typeof options.plural === 'function') {
			return options.plural(0);
		} else if (options.hasOwnProperty('hideValue')) {
			return options.plural;
		} else {
			return `${0} ${options.plural}`;
		}
	}
}

export function csvEncode(value?: string) {
	return value?.includes('"') ? strReplaceAll(value, '"', '""') : value;
}

/**
 * Converts a value to a friendly string representation.
 * - If the value is null, undefined, or an empty string, it returns null.
 * - If the value is a string or number, it returns its string representation.
 * - If the value is an array, it recursively converts each element and joins them with a comma.
 * - For other types (like objects), it returns a JSON string representation.
 *
 * @param {unknown} value - The value to convert.
 * @returns {string | null} - The friendly string representation or null if the value is falsey (except for zero).
 */
export function friendlyStringify(value: unknown): string | null {
	// falsey values should be excluded, except zero
	if (value === 'null' || (!value && value !== 0)) return null;

	return typeof value === 'string' || typeof value === 'number'
		? value.toString()
		: Array.isArray(value)
			? value
					.map(friendlyStringify)
					.filter(a => a !== null)
					.join(', ')
			: // we can't really handle objects, we are expecting plain strings or arrays of strings
				JSON.stringify(value);
}
/** Function to help determine prepositions for nouns eg whether to use 'a' or 'an' */
/** IMPORTANT: this is only currently used for entity types/owners like "event" and "post".
 * This shouldn't be used in every case as it ignores edge cases like 'hour' or 'unique'
 * (for those curious, the rule is about how the letter "sounds" - but putting that into a function is a job for another day)
 */
export const firstCharIsVowel = (input: string) => {
	return VOWELS.includes(input.charAt(0));
};
